// src/components/vault/VaultFlatTable.jsx  (UPDATED: adds "View" button)
import React from "react";
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Button, Stack, Typography, Chip, IconButton, Box
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import themeColors from "../../theme";
import { formatBytes, formatDateYMD } from "./fileUtils";

const SHORT_LABEL = (k) =>
    ({
        legal: "Legal",
        education: "Education",
        licenses: "Licenses",
        resuscitation: "Resuscitation",
        logs: "Logs",
        employment: "Employment",
        health: "Health",
        other: "Other"
    }[k] || "Other");

const CATEGORY_COLOR = (k) => {
    const colors = {
        resuscitation: { bg: "#E0F2F1", text: "#00695C" },
        employment: { bg: "#E3F2FD", text: "#1565C0" },
        licenses: { bg: "#FFF3E0", text: "#E65100" },
        education: { bg: "#F3E5F5", text: "#6A1B9A" },
        health: { bg: "#FCE4EC", text: "#C2185B" },
        logs: { bg: "#FFF9C4", text: "#F57F17" },
        legal: { bg: "#E8EAF6", text: "#283593" },
        other: { bg: "#F5F5F5", text: "#616161" }
    };
    return colors[k] || colors.other;
};

export default function VaultFlatTable({ docs, onView, onEdit, onDelete, onSend }) {
    const brand = themeColors?.teal || themeColors?.primary || "#007F7E";
    const buttonSx = {
        textTransform: "none",
        fontWeight: 600,
        px: 2,
        py: 0.75,
        borderRadius: 1.5,
        minWidth: 80
    };

    return (
        <TableContainer>
            <Table>
                <TableHead>
                    <TableRow sx={{ backgroundColor: "#FAFAFA" }}>
                        <TableCell sx={{ fontWeight: 600, color: "text.secondary" }}>Category</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: "text.secondary", width: "35%" }}>Document Name</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: "text.secondary" }}>Upload Date</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: "text.secondary" }}>Expiration Date</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: "text.secondary" }} align="right">Actions</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {docs.map((d) => {
                        const categoryColor = CATEGORY_COLOR(d.category);
                        return (
                            <TableRow key={d.id} hover sx={{ '&:hover': { backgroundColor: "#F9FAFB" } }}>
                                <TableCell>
                                    <Chip
                                        label={SHORT_LABEL(d.category)}
                                        size="small"
                                        sx={{
                                            backgroundColor: categoryColor.bg,
                                            color: categoryColor.text,
                                            fontWeight: 600,
                                            fontSize: "0.75rem",
                                            height: 28,
                                            borderRadius: 1
                                        }}
                                    />
                                </TableCell>
                                <TableCell>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <DescriptionOutlinedIcon sx={{ color: "text.secondary", fontSize: 20 }} />
                                        <Box>
                                            <Typography sx={{ fontWeight: 600, fontSize: "0.95rem" }}>{d.name}</Typography>
                                            <Typography variant="caption" color="text.secondary">{formatBytes(d.size)}</Typography>
                                        </Box>
                                    </Stack>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2">{d.uploadedAt ? formatDateYMD(d.uploadedAt) : "—"}</Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2" sx={{ color: d.expiresAt ? "#F57C00" : "text.secondary" }}>
                                        {d.expiresAt ? formatDateYMD(d.expiresAt) : "—"}
                                    </Typography>
                                </TableCell>
                                <TableCell align="right">
                                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                                        <Button
                                            variant="contained"
                                            onClick={() => onSend?.(d)}
                                            startIcon={<MailOutlineIcon sx={{ fontSize: 18 }} />}
                                            sx={{
                                                ...buttonSx,
                                                backgroundColor: brand,
                                                color: "#fff",
                                                "&:hover": { backgroundColor: themeColors?.darkTeal || brand }
                                            }}
                                        >
                                            Send
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            onClick={() => onView?.(d.id)}
                                            sx={{
                                                ...buttonSx,
                                                borderColor: brand,
                                                color: brand,
                                                "&:hover": { backgroundColor: "#F0F7F7", borderColor: brand }
                                            }}
                                        >
                                            View
                                        </Button>
                                        <Button
                                            variant="text"
                                            onClick={() => onEdit?.(d)}
                                            sx={{
                                                ...buttonSx,
                                                color: "text.secondary",
                                                "&:hover": { backgroundColor: "#F5F5F5" }
                                            }}
                                        >
                                            Edit
                                        </Button>
                                        <IconButton
                                            onClick={() => onDelete?.(d.id)}
                                            sx={{ color: "#EF5350" }}
                                            size="small"
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Stack>
                                </TableCell>
                            </TableRow>
                        );
                    })}
                    {docs.length === 0 && (
                        <TableRow>
                            <TableCell colSpan={5} align="center" sx={{ py: 6, color: "text.secondary" }}>
                                <Typography variant="body1">No documents yet.</Typography>
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );
}